import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import './SignUp.css';
import LanguageContext from './LanguageContext';
import { signupPageCopy } from './translations';

function SignUp() {
  const { language } = useContext(LanguageContext);
  const copy = signupPageCopy[language] || signupPageCopy.English;
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = copy.errors.fullNameRequired;
    }

    if (!formData.email.trim()) {
      newErrors.email = copy.errors.emailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = copy.errors.emailInvalid;
    }

    if (!formData.password) {
      newErrors.password = copy.errors.passwordRequired;
    } else if (formData.password.length < 8) {
      newErrors.password = copy.errors.passwordLength;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = copy.errors.confirmPasswordRequired;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = copy.errors.passwordsMismatch;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    if (validateForm()) {
      setIsLoading(true);
      
      // Verify supabase is available
      if (!supabase) {
        console.error('Supabase client is not initialized');
        setSubmitError(copy.errors.dbConnection);
        setIsLoading(false);
        return;
      }
      
      try {
        // Check if email already exists
        const { data: existingUsers, error: checkError } = await supabase
          .from('users')
          .select('email')
          .eq('email', formData.email.toLowerCase());
        
        if (checkError && checkError.code !== 'PGRST116') {
          // PGRST116 is "no rows returned" which is fine
          console.error('Error checking email:', checkError);
        }
        
        if (existingUsers && existingUsers.length > 0) {
          setErrors({ email: copy.errors.emailExists });
          setIsLoading(false);
          return;
        }
        
        // Insert new user into Supabase
        const { data, error } = await supabase
          .from('users')
          .insert([
            {
              full_name: formData.fullName.trim(),
              email: formData.email.toLowerCase().trim(),
              password: formData.password // Note: In production, hash passwords before storing
            }
          ])
          .select();
        
        if (error) {
          console.error('Sign up error:', error);
          setSubmitError(copy.errors.signupFailed);
          setIsLoading(false);
          return;
        }
        
        // Success
        console.log('Sign up successful:', data);
        // Navigate to login page
        navigate('/login', { state: { message: copy.successMessage } });
      } catch (err) {
        console.error('Unexpected error:', err);
        setSubmitError(copy.errors.unexpected);
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Sign Up Form */}
        <div className="auth-form-wrapper">
          <h2 className="auth-title">{copy.title}</h2>
          <p className="auth-subtitle">{copy.subtitle}</p>

          {submitError && (
            <div className="submit-error-message" style={{
              backgroundColor: 'rgba(236, 72, 153, 0.1)',
              border: '1px solid rgba(236, 72, 153, 0.3)',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '1.5rem',
              color: '#EC4899',
              fontSize: '0.9rem',
              textAlign: 'center'
            }}>
              {submitError}
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <input
                type="text"
                name="fullName"
                id="fullName"
                placeholder={copy.fullNamePlaceholder}
                value={formData.fullName}
                onChange={handleChange}
                className={`form-input ${errors.fullName ? 'error' : ''}`}
              />
              {errors.fullName && <span className="error-message">{errors.fullName}</span>}
            </div>

            <div className="form-group">
              <input
                type="email"
                name="email"
                id="email"
                placeholder={copy.emailPlaceholder}
                value={formData.email}
                onChange={handleChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <input
                type="password"
                name="password"
                id="password"
                placeholder={copy.passwordPlaceholder}
                value={formData.password}
                onChange={handleChange}
                className={`form-input ${errors.password ? 'error' : ''}`}
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>

            <div className="form-group">
              <input
                type="password"
                name="confirmPassword"
                id="confirmPassword"
                placeholder={copy.confirmPasswordPlaceholder}
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
              />
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
            </div>

            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? copy.creating : copy.signupButton}
            </button>
          </form>

          {/* Alternate Option */}
          <div className="auth-alternate">
            <p>
              {copy.alternateText}{' '}
              <Link to="/login" className="auth-link">{copy.login}</Link>
            </p>
          </div>
        </div>

        {/* Privacy Note */}
        <div className="auth-footer">
            <p className="privacy-note">
              {copy.privacyNote}
            </p>
        </div>
      </div>
    </div>
  );
}

export default SignUp;



